<template>
  <section-collapseable
    ref="section"
    title="Buying Info"
    :sub-title="subtitle"
    :collapseable="isSet"
    hide-initial-when-expanded
    :is-loading="isLoading"
    second-button-text="View Comps"
    @secondButtonClick="openCompInfoModal"
  >
    <!-- Default Section -->
    <buying-info-flip-analysis v-if="isFlip" is-summary />
    <buying-info-wholesale-analysis v-else-if="isWholesale" is-summary />
    <buying-info-novation-analysis v-else-if="isNovation" is-summary />
    <buying-info-retail-listing-analysis v-else-if="isRetailListing" is-summary />

    <form-field-select
      v-else-if="isNotSet"
      label="Would you like to buy via wholesale or fix & flip?"
      :value="analysisType"
      @input="setAnalysisType"
      :loading="typeIsLoading"
      :disabled="typeIsLoading"
    >
      <option :value="null">None</option>
      <option
        v-for="type in ENUMS.BuyingAnalysisTypeEnum"
        :key="type.value"
        :value="type.value"
      >
        {{ type.label }}
      </option>
    </form-field-select>

    <!-- Expanded Section -->
    <template v-slot:expanded>
      <form-field-select
        label="Buying Type"
        :value="analysisType"
        @input="setAnalysisType"
        :loading="typeIsLoading"
        :disabled="typeIsLoading"
      >
        <option :value="null">None</option>
        <option
          v-for="type in ENUMS.BuyingAnalysisTypeEnum"
          :key="type.value"
          :value="type.value"
        >
          {{ type.label }}
        </option>
      </form-field-select>

      <hr />
      <buying-info-flip-analysis v-if="isFlip" />
      <buying-info-wholesale-analysis v-else-if="isWholesale" />
      <buying-info-novation-analysis v-else-if="isNovation" />
      <buying-info-retail-listing-analysis v-else-if="isRetailListing" />
    </template>

    <modal-container>
      <!-- Property Comps Modal -->
      <property-comps-modal
        ref="compsModal"
        :property="property"
      ></property-comps-modal>
    </modal-container>
  </section-collapseable>
</template>

<script>
import {ENUMS} from "../../../services/enums";
import BuyingInfoFlipAnalysis from "../components/BuyingInfoFlipAnalysis";
import BuyingInfoWholesaleAnalysis from "../components/BuyingInfoWholesaleAnalysis";
import InfoHeading from "../components/InfoHeading";
import InfoItem from "../components/InfoItem";
import ProfitItem from "../components/ProfitItem";
import api from "../../../api";
import PropertyCompsModal from "../PropertyCompsModal.vue";
import BuyingInfoNovationAnalysis from "../components/BuyingInfoNovationAnalysis.vue";
import BuyingInfoRetailListingAnalysis from "../components/BuyingInfoRetailListingAnalysis.vue";

export default {
  name: "LeadBuyingInfo",
  components: {
    BuyingInfoRetailListingAnalysis,
    BuyingInfoNovationAnalysis,
    PropertyCompsModal,
    ProfitItem,
    BuyingInfoWholesaleAnalysis,
    BuyingInfoFlipAnalysis,
    InfoHeading,
    InfoItem,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      statusIsSaving: false,
      typeIsLoading: false,
    }
  },
  computed: {
    record() {
      // If activeRecord is null then return necessary empty info
      return this.$store.state.activeRecord ? this.$store.state.activeRecord.record : {
        id: null,
        buying_info_status: null,
        buying_analysis_id: null,
        buying_analysis_type: null,
        buying_info_accepted_at: null,
        buying_info_closing_at: null,
        buying_info_contracted_at: null,
      };
    },
    property() {
      return this.$store.state.activeRecord.property
    },
    analysisType() {
      return this.record.buying_analysis_type;
    },
    isNotSet() {
      return this.analysisType === null;
    },
    isSet() {
      return !!this.analysisType;
    },
    isFlip() {
      return this.analysisType === ENUMS.BuyingAnalysisTypeEnum.FixAndFlip.value;
    },
    isWholesale() {
      return this.analysisType === ENUMS.BuyingAnalysisTypeEnum.Wholesale.value;
    },
    isNovation() {
      return this.analysisType === ENUMS.BuyingAnalysisTypeEnum.Novation.value;
    },
    isRetailListing() {
      return this.analysisType === ENUMS.BuyingAnalysisTypeEnum.RetailListing.value;
    },
    subtitle() {
      if(this.isFlip) {
        return ENUMS.BuyingAnalysisTypeEnum.FixAndFlip.label;
      }

      if(this.isWholesale) {
        return ENUMS.BuyingAnalysisTypeEnum.Wholesale.label;
      }

      if (this.isNovation) {
        return ENUMS.BuyingAnalysisTypeEnum.Novation.label;
      }

      if (this.isRetailListing) {
        return ENUMS.BuyingAnalysisTypeEnum.RetailListing.label;
      }
    },
  },
  methods: {
    openCompInfoModal() {
      this.$refs.compsModal.open()
    },
    setAnalysisType(type) {
      this.typeIsLoading = true;

      if (type === null) {
        api.records.setActiveBuyingAnalysis(this.record.id, {})
        .finally(() => {
          this.typeIsLoading = false
          this.$refs.section.toggleSection();
        });
      }

      if(type === ENUMS.BuyingAnalysisTypeEnum.FixAndFlip.value) {
        const analysis = this.$store.state.activeRecord.fixAndFlipAnalysis;

        if(analysis) {
          api.records.setActiveBuyingAnalysis(this.record.id, analysis)
          .finally(() => this.typeIsLoading = false);
        } else {
          api.analysis.createFixAndFlipAnalyses({
            record_id: this.record.id,
            after_repair_value: null,
            repair_costs: null,
            holding_costs: null,
            holding_time: null,
            other_costs: null,
            desired_profit: null,
          }).finally(() => this.typeIsLoading = false);
        }
      }

      if(type === ENUMS.BuyingAnalysisTypeEnum.Wholesale.value) {
        const analysis = this.$store.state.activeRecord.wholesaleAnalysis;

        if(analysis) {
          api.records.setActiveBuyingAnalysis(this.record.id, analysis)
          .finally(() => this.typeIsLoading = false);
        } else {
          api.analysis.createWholesaleAnalyses({
            record_id: this.record.id,
            after_repair_value: null,
            desired_wholesale_fee: null,
            offer_percentage: null,
            other_costs: null,
            repair_costs: null,
          }).finally(() => this.typeIsLoading = false);
        }
      }

      if (type === ENUMS.BuyingAnalysisTypeEnum.Novation.value) {
        const analysis = this.$store.state.activeRecord.novationAnalysis;

        if (analysis) {
          api.records.setActiveBuyingAnalysis(this.record.id, analysis)
            .finally(() => this.typeIsLoading = false);
        } else {
          api.analysis.novationAnalysis.create({
            record_id: this.record.id,
            after_repair_value: null,
            repair_costs: null,
            holding_costs: null,
            holding_time: null,
            other_costs: null,
            desired_profit: null,
          }).finally(() => this.typeIsLoading = false);
        }
      }

      if (type === ENUMS.BuyingAnalysisTypeEnum.RetailListing.value) {
        const analysis = this.$store.state.activeRecord.retailListingAnalysis;

        if (analysis) {
          api.records.setActiveBuyingAnalysis(this.record.id, analysis)
            .finally(() => this.typeIsLoading = false);
        } else {
          api.analysis.retailListingAnalysis.create({
            record_id: this.record.id,
            retail_value: null,
            agent_commission: null,
            mortgage_payoff: null,
            holding_costs: null,
            other_costs: null,
            desired_profit: null,
          }).finally(() => this.typeIsLoading = false);
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

hr {
  margin: 0.75rem (-$modalGutter);
}
</style>
